import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import { cn } from "@/lib/utils";
import { ChevronRight, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cardClassName, rowDividerClassName } from "./assistantClasses";

type AgendaTimeSlotsResponseProps = {
    timeSlots: AgendaTimeSlot[];
    /**
     * For each slot, in the same order: the message a click sends (step-based contract), as if the
     * user had typed it. Absent: the slot is only shown.
     */
    replies?: (string | undefined)[];
    /** A later message exists: the slots are no longer offered, they are only shown. */
    isPast?: boolean;
};

/**
 * One free slot, the day written once: "mer. 7 oct. · 14:00 → 15:00". A slot that spans two
 * days gives both days.
 */
export const formatTimeSlot = (
    start: Date,
    end: Date,
    language: string,
): string => {
    const day = (date: Date) =>
        date.toLocaleDateString(language, {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
    const hour = (date: Date) =>
        date.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });
    const sameDay = start.toDateString() === end.toDateString();

    return sameDay
        ? `${day(start)} · ${hour(start)} → ${hour(end)}`
        : `${day(start)} · ${hour(start)} → ${day(end)} · ${hour(end)}`;
};

const rowClassName =
    "flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-primary-darkest";

/**
 * The free slots of an answer, one line each. Offered by the assistant (`replies`), a slot is picked
 * with a click: the click sends its message, and the assistant then proposes the meeting — nothing
 * is created by the click.
 */
const AgendaTimeSlotsResponse = ({
    timeSlots,
    replies,
    isPast = false,
}: AgendaTimeSlotsResponseProps) => {
    const { i18n } = useTranslation();
    const { onAnswerQuestion } = useVistassistantMail();

    return (
        <div
            className={cn(
                cardClassName,
                "flex w-full flex-col overflow-hidden",
            )}
        >
            {timeSlots.map((timeSlot, index) => {
                const reply = replies?.[index];
                const content = (
                    <>
                        <Clock
                            aria-hidden
                            className="h-3.5 w-3.5 flex-none text-primary-dark"
                        />
                        {formatTimeSlot(
                            timeSlot.startDate,
                            timeSlot.endDate,
                            i18n.language,
                        )}
                    </>
                );
                const key =
                    timeSlot.startDate.toISOString() +
                    timeSlot.endDate.toISOString();

                return reply && !isPast && onAnswerQuestion ? (
                    <button
                        key={key}
                        className={cn(
                            rowDividerClassName,
                            rowClassName,
                            "cursor-pointer transition-colors hover:bg-assistant-chip/60",
                        )}
                        onClick={() => onAnswerQuestion(reply)}
                        type="button"
                    >
                        {content}
                        {/* A slot that can be picked says so: a discreet arrow at the end of its line. */}
                        <ChevronRight
                            aria-hidden
                            className="ml-auto h-4 w-4 flex-none text-assistant-muted"
                        />
                    </button>
                ) : (
                    <div
                        key={key}
                        className={cn(rowDividerClassName, rowClassName)}
                    >
                        {content}
                    </div>
                );
            })}
        </div>
    );
};

export default AgendaTimeSlotsResponse;
